/**
 * src/data/planes.js
 * La tabla de planes del panel. Una sola vez, en un sitio.
 *
 * Vivía dentro de Pricing.jsx, así que la pantalla de alta no tenía de dónde
 * leer el precio del plan que el visitante acaba de pulsar en la landing — y la
 * salida fácil habría sido copiarla otra vez. Eso ya lo pagamos caro con
 * `guardar_snapshot` y con las catorce copias del prefijo `/scraping`: dos
 * copias funcionan igual el día que se escriben y divergen el día que alguien
 * toca una (ver docs/ERRORES_RECURRENTES.md §4).
 *
 * ⚠️ Lo que este módulo NO resuelve, y conviene saberlo: la landing
 * (repo cm-landing, `src/routes/index.tsx`) mantiene su propia copia porque es
 * otro despliegue y no puede importar de aquí. Son DOS fuentes, no una. Si
 * cambia un precio hay que mover las dos en el mismo día, y el candidato para
 * cerrarlo de verdad es servir la tabla desde el backend — hoy no existe:
 * `app/services/tier_limits.py` guarda límites y features, pero NO precios.
 *
 * ⚠️ Y de ahí sale la asimetría que ya nos mordió el 10-09-2026: para los
 * PRECIOS esta tabla es fuente; para los LÍMITES que se citan en las features
 * (familias, empresas propias, usuarios, productos) NO lo es — manda
 * `app/services/tier_limits.py`, que además es quien devuelve el 409 al
 * cliente. Ese día el tope de familias de Pro subió de 6 a 8 en tier_limits.py
 * y aquí se quedó el 6: el alta seguía prometiendo menos de lo que el producto
 * ya daba. Al tocar un número de estas features, comprobar tier_limits.py
 * PRIMERO y mover la landing el mismo día.
 */

/** El anual regala UN mes: se pagan 11, se usan 12. Eran 2 meses hasta la
 *  tabla v3 (Manuel, 28-10-2026). */
export const MESES_ANUAL = 11

/**
 * Precio mensual en UF. Tarifa de lista.
 *
 * 📌 Tabla v3, cerrada por Manuel el 28-10-2026 y ya publicada en cmmonitor.cl
 * (`546fccf` del repo de la landing). Eran 1,25 / 2,50 / 5,50.
 *
 * 🔑 Y aquí solo están los planes que se ANUNCIAN. La ficha de Enterprise vive
 * debajo, en `PLAN_A_MEDIDA`, y NO entra en este array a propósito: `Pricing.jsx`
 * recorre `PLANES`, así que sacarla de aquí es lo que la retira del escaparate sin
 * tocar un fichero de otra zona. Sigue siendo localizable por `buscarPlan()`,
 * porque un tenant que YA esté en Enterprise necesita que su propia pantalla sepa
 * describir su plan.
 *
 * 🔴 Lo que estos tres números NO arreglan: **Flow**. Lo que se cobra son los seis
 * planes `FLOW_PLAN_*` configurados a mano, y mientras no se muevan, esta ficha
 * promete 2 / 3,8 / 6,5 y el cargo es 1,25 / 2,50 / 5,50. Ninguna prueba puede
 * verlo: Flow está detrás de la red y de un secreto.
 */
export const PLANES = [
  {
    id: 'basic', nombre: 'Basic', precio: 2,
    desc: 'Para el especialista: monitorea tu catálogo a diario.',
    features: [
      '1 empresa propia',
      'Hasta 4 familias/convenios en Convenio Marco',
      'Monitoreo para catálogos de hasta ~1.000 productos',
      'Actualización de precios diaria',
      'Comparación de precios vs. 5 competidores, con sus nombres',
      'Alertas por email + resumen semanal por email',
      'Historial de datos de 180 días',
    ],
    featured: false,
  },
  {
    id: 'pro', nombre: 'Pro', precio: 3.8,
    desc: 'Para operar en serio: competencia y simulador.',
    heredaDe: 'Basic',
    features: [
      'Módulo Competencia: ficha por rival, ranking y solape con tu catálogo',
      'Simulador de precios para posicionar productos',
      '1 empresa propia · 8 competidores · 8 familias · catálogos de ~2.500 productos',
      'Resumen semanal con las ofertas nuevas de tus competidores',
      'Historial de 12 meses · 2 usuarios del panel',
    ],
    featured: true,
  },
  {
    id: 'premium', nombre: 'Premium', precio: 6.5,
    desc: 'Catálogo amplio: predicción y todas tus familias.',
    heredaDe: 'Pro',
    features: [
      'Predictor de oportunidades de compra (CM, CA, TD…)',
      'Todas tus familias/convenios, sin límite',
      '15 competidores vigilados · 3 empresas propias',
      'Catálogos de hasta 20.000 productos',
      'Historial de 24 meses · 5 usuarios del panel',
      'Soporte técnico prioritario',
    ],
    featured: false,
  },
]

/**
 * La ficha del plan a medida. FUERA de `PLANES` desde el 28-10-2026: la tabla v3
 * lo retira de la venta pública, pero el tier sigue vivo en el motor
 * (`tier_limits.LIMITES` y `TIERS_PUBLICOS` explican por qué borrarlo sería un
 * fallo: un cliente Enterprise resolvería a nivel 0).
 *
 * Se conserva porque sigue habiendo dos usos legítimos, los dos de lectura:
 *   · `buscarPlan('enterprise')` — un tenant ya provisionado así necesita que
 *     `ResumenPlan` sepa decir qué plan tiene.
 *   · es la vía de la venta asistida para el catálogo de más de 20.000 productos,
 *     al que la landing manda con «lo vemos contigo».
 *
 * 🔴 SIN `precio` NI `desde`, a propósito (Manuel, 31-10-2026: «el tier
 * enterprise desaparece completamente, así es que no enseña ningún precio»).
 * Conservaba `precio: 9` y `desde: true`, y la ficha del alta pintaba «desde
 * 8,25 UF/mes» (9 × 11/12) junto a «Precio a medida»: aritmética correcta sobre
 * un precio que la v3 ya había retirado. Quien pinte un plan tiene que preguntar
 * `tienePrecio(plan)` antes de hacer cuentas: `precioPorMes()` y `precioAnual()`
 * devuelven `null` sin precio, y `fmtUF(null)` revienta.
 */
export const PLAN_A_MEDIDA = {
  id: 'enterprise', nombre: 'Enterprise', publico: false,
  desc: 'Gran catálogo: sin límites y acompañamiento.',
  heredaDe: 'Premium',
  features: [
    'Catálogos sobre 20.000 productos, sin techo',
    '10 usuarios del panel',
    'Onboarding dedicado e informe ejecutivo',
    'Plan y precio a medida',
  ],
  featured: false,
}

/**
 * Programa Pionero, REFORMADO por Manuel el 28-10-2026: ya no es «tarifa congelada
 * 24 meses sin rebaja» sino **25 % menos durante 12 meses, solo en el plan anual**,
 * y al renovar, tarifa de lista.
 *
 * Va solo en anual porque es lo que lo hace operable: el descuento vive en su propio
 * plan de Flow y a un pionero mensual habría que MOVERLO de plan al mes 12, cosa que
 * hoy no hace nadie — se quedaría con el 25 % para siempre sin que saltara ninguna
 * alarma. Pagando por adelantado, ese trabajo lo hace el vencimiento.
 */
export const PIONERO_DESCUENTO_PCT = 25
export const PIONERO_MESES_DESCUENTO = 12
export const PIONERO_SOLO_ANUAL = true

/** Días de prueba. Espejo de TRIAL_DIAS en app/routers/billing.py. */
export const DIAS_TRIAL = 14

/**
 * El id que llega por la URL puede venir de cualquier sitio: null si no existe.
 *
 * ⚠️ Busca también en `PLAN_A_MEDIDA`, y no es un detalle: si solo mirara `PLANES`,
 * un tenant de Enterprise que entre a su alta o a su cuenta se encontraría la ficha
 * en blanco el día que ese plan salió del escaparate.
 */
export function buscarPlan(id) {
  if (!id) return null
  const buscado = String(id).toLowerCase()
  return [...PLANES, PLAN_A_MEDIDA].find(p => p.id === buscado) || null
}

/** ¿Tiene el plan un precio publicado? Enterprise no (ver `PLAN_A_MEDIDA`). */
export function tienePrecio(plan) {
  return typeof plan?.precio === 'number'
}

/** Lo que se paga cada mes según el ciclo. El anual reparte 11 pagos en 12 meses.
 *  `null` si el plan no tiene precio: mejor nada que «NaN UF/mes». */
export function precioPorMes(plan, anual) {
  if (!tienePrecio(plan)) return null
  return anual ? (plan.precio * MESES_ANUAL) / 12 : plan.precio
}

/** Lo que se cobra de una vez en el ciclo anual. `null` si el plan no tiene precio. */
export function precioAnual(plan) {
  if (!tienePrecio(plan)) return null
  return plan.precio * MESES_ANUAL
}

/** 1.25 → "1,25" · 2.5 → "2,5" · 25 → "25" */
export function fmtUF(n) {
  return n.toFixed(2).replace(/\.?0+$/, '').replace('.', ',')
}
